import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';
import { formatClinicDateTime } from '../../utils/clinicTime';
import { VITAL_FIELD_UNITS, vitalFieldLabel } from '../../utils/visitAudit';

// Vitals changed after they were first recorded. Every row is a VITALS_EDITED audit entry, written
// only when a visit already carried a recording timestamp, so the reading had been committed and
// possibly already acted on before it moved. Read-only: nothing here recalculates anything.
//
// The question this answers is not "who mistyped a number". It is "did a measurement the doctor
// may have decided on change underneath them, and was there a good reason" — which is why the
// reason column is never blank and why the after-the-doctor-was-called count leads the KPIs.

const VITAL_UNITS = VITAL_FIELD_UNITS;
const vitalLabel = vitalFieldLabel;

// A blank reading is meaningfully different from a zero one: "was not taken, now 98" is a different
// event from "was 98, now 99", so an absent value reads as a dash rather than as a number.
const vitalValue = (v, field) =>
  v === null || v === undefined || v === '' ? '-' : `${v} ${VITAL_UNITS[field] || ''}`.trim();

const COLUMNS = [
  {
    key: 'changedAt', label: 'Changed At',
    render: (r) => formatClinicDateTime(r.changedAt),
    csv: (r) => r.changedAt,
  },
  { key: 'token', label: 'Token', render: (r) => <span className="font-mono">{r.token}</span> },
  { key: 'patient', label: 'Patient', wrap: true },
  { key: 'mrn', label: 'MRN', render: (r) => <span className="font-mono text-xs">{r.mrn}</span> },
  { key: 'doctor', label: 'Doctor', wrap: true, render: (r) => r.doctor || '-' },
  {
    key: 'field', label: 'Measurement',
    render: (r) => vitalLabel(r.field),
    csv: (r) => vitalLabel(r.field),
  },
  {
    key: 'oldValue', label: 'From',
    render: (r) => (
      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-mono text-xs whitespace-nowrap">
        {vitalValue(r.oldValue, r.field)}
      </span>
    ),
    csv: (r) => vitalValue(r.oldValue, r.field),
  },
  {
    key: 'newValue', label: 'To',
    render: (r) => (
      <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-md font-mono text-xs whitespace-nowrap">
        {vitalValue(r.newValue, r.field)}
      </span>
    ),
    csv: (r) => vitalValue(r.newValue, r.field),
  },
  { key: 'reason', label: 'Reason', wrap: true, render: (r) => r.reason || '-' },
  { key: 'changedBy', label: 'Changed By', wrap: true, render: (r) => r.changedBy || '-' },
];

const VitalsEditsReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getVitalsEdits({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const afterCalled = Number(k.afterDoctorCalled || 0);
  const kpis = [
    { label: 'Edits', value: k.edits ?? 0, sub: 'Separate amendments' },
    { label: 'Measurements', value: k.measurements ?? 0, sub: 'Individual readings changed' },
    { label: 'Patients', value: k.patients ?? 0, sub: 'Distinct visits affected' },
    {
      label: 'After Doctor Called',
      value: afterCalled,
      sub: 'Changed once the patient was with the doctor',
      // The one number worth acting on: the doctor may have decided on the earlier reading.
      tone: afterCalled > 0 ? 'alert' : undefined,
    },
    { label: 'Staff', value: k.staff ?? 0, sub: 'People who made a change' },
  ];

  const chartRows = useMemo(
    () => (data?.series || []).map((s) => ({
      field: vitalLabel(s.field),
      changes: Number(s.changes),
    })),
    [data]
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="Which measurements get corrected after they were recorded">
        {loading || chartRows.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No vitals were changed in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 44)}>
            <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="field" {...axisProps} width={150} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="changes" radius={[0, 4, 4, 0]} barSize={18}>
              {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Every vitals change"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`vitals-edits-${range.from}-to-${range.to}`}
        emptyText="No vitals were changed after recording in the selected range"
        footnote="A row appears only when a reading was altered after it had already been saved. The first recording of a patient's vitals is not an edit."
      />

      <ReportPrintLayout
        title="Vitals Changed After Recording"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[{ title: 'Every vitals change', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default VitalsEditsReport;
